import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Popover } from 'antd'

import { IPagination } from './types'
import { Button } from '../button'

import { IconLeft, IconRight } from '@img/icons'
import css from './index.module.scss'
import { Container } from '@ui/container'


/** Компонент Pagination */
export const Pagination = ({ totalCount = 0, pageSize = 0, currentPage = 0, size = 'sm', onChange }: IPagination) => {
    const triggerRef = useRef<HTMLSpanElement>(null)
    const [isExpanded, setIsExpanded] = useState(false)
    const [popupMaxHeight, setPopupMaxHeight] = useState(250)
    const totalPages = pageSize > 0 ? Math.max(0, Math.ceil(totalCount / pageSize)) : 0

    const getDisabledButtons = () => {
        let btnLeft: boolean = currentPage === 1 ? true : false
        let btnRight: boolean = (totalCount / pageSize) <= currentPage ? true : false
        let btnPrimary: boolean = (totalCount / pageSize) <= 1 ? true : false

        if (totalCount === 0 || pageSize === 0) {
            btnLeft = true
            btnRight = true
            btnPrimary = true
        }

        return { btnLeft, btnRight, btnPrimary }
    }

    const updatePopupHeight = useCallback(() => {
        const bounds = triggerRef.current?.getBoundingClientRect()
        if (!bounds) return
        const viewportHeight = window.visualViewport?.height || window.innerHeight
        setPopupMaxHeight(Math.max(0, Math.min(250, Math.max(bounds.top, viewportHeight - bounds.bottom) - 16)))
    }, [])

    useEffect(() => {
        if (!isExpanded) return
        updatePopupHeight()
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsExpanded(false)
                triggerRef.current?.querySelector('button')?.focus()
            }
        }
        window.addEventListener('resize', updatePopupHeight)
        window.visualViewport?.addEventListener('resize', updatePopupHeight)
        document.addEventListener('keydown', handleKeyDown)

        return () => {
            window.removeEventListener('resize', updatePopupHeight)
            window.visualViewport?.removeEventListener('resize', updatePopupHeight)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [isExpanded, updatePopupHeight])

    useEffect(() => {
        if (totalPages <= 1) setIsExpanded(false)
    }, [totalPages])

    return (
        <div className={css.pagination}>
            <Button size={size} variant='icon' isDisabled={getDisabledButtons().btnLeft} onClick={() => { onChange && onChange(currentPage - 1) }}>
                <IconLeft width='16px' height='16px' />
            </Button>

            <Popover
                trigger='click'
                placement='topRight'
                arrow={false}
                open={isExpanded && totalPages > 1}
                onOpenChange={open => {
                    if (open) updatePopupHeight()
                    setIsExpanded(open && !getDisabledButtons().btnPrimary)
                }}
                getPopupContainer={() => document.body}
                autoAdjustOverflow
                destroyOnHidden
                styles={{ container: { padding: 0, background: 'transparent', boxShadow: 'none' }, content: { padding: 0 } }}
                content={
                    <div className={css.pagination_list} style={{ maxHeight: popupMaxHeight }} role='navigation' aria-label='Выбор страницы'>
                        <Container padding='4px' className={css.pagination_list__content}>
                            {Array.from({ length: totalPages }, (_, index) => (
                                currentPage !== (index + 1) && (
                                    <Button variant='icon' size='sm' key={index + 1} onClick={() => { onChange && onChange(index + 1); setIsExpanded(false) }}>Стр. {index + 1}</Button>
                                )
                            ))}
                        </Container>
                    </div>
                }
            >
                <span className={css.pagination_trigger} ref={triggerRef}>
                    <Button size={size} variant='icon' padding='0 12px' isDisabled={getDisabledButtons().btnPrimary}>
                        Стр. {currentPage}
                    </Button>
                </span>
            </Popover>

            <Button size={size} variant='icon' isDisabled={getDisabledButtons().btnRight} onClick={() => { onChange && onChange(currentPage + 1) }}>
                <IconRight width='16px' height='16px' />
            </Button>
        </div>
    )
}
